<script setup>
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useDealsStore } from 'dashboard/stores/deals';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import DealDetailsDialog from 'dashboard/components-next/Deals/DealDetailsDialog.vue';

const props = defineProps({
  conversationId: { type: Number, default: null },
  contactId: { type: Number, default: null },
  companyId: { type: Number, default: null },
  // Abas de Contato/Empresa: linha inteira destacada como clicavel (hover + seta)
  highlightRows: { type: Boolean, default: false },
});

const emit = defineEmits(['dealsChanged']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const dealsStore = useDealsStore();
const getAgentById = useMapGetter('agents/getAgentById');

const contactDeals = ref([]);
const dealDetailsDialogRef = ref(null);

const hasDeals = computed(() => contactDeals.value.length > 0);

const loadContactDeals = async () => {
  if (props.contactId) {
    contactDeals.value = await dealsStore.getByContact(props.contactId);
  } else if (props.companyId) {
    contactDeals.value = await dealsStore.getByCompany(props.companyId);
  } else {
    contactDeals.value = [];
  }
  emit('dealsChanged', contactDeals.value.length);
};

// Criar negociacao: na coluna esquerda quando ha LinkedEntityPane, senao na
// pagina de criacao (contato/empresa/conversa vao pre-preenchidos).
const openNewDealInPlace = inject('openNewDealInPlace', null);

const openCreateDialog = () => {
  if (openNewDealInPlace) {
    openNewDealInPlace({
      contactId: props.contactId,
      companyId: props.companyId,
      conversationId: props.conversationId,
    });
    return;
  }
  router.push({
    name: 'deals_new',
    params: { accountId: route.params.accountId },
    query: {
      contact_id: props.contactId || undefined,
      company_id: props.companyId || undefined,
      conversation_id: props.conversationId || undefined,
    },
  });
};

// Dentro de Contato/Empresa/Negociacao a negociacao abre na coluna esquerda;
// em qualquer outro lugar (ex.: painel da Conversa) abre no painel lateral.
const openLinkedInPlace = inject('openLinkedInPlace', null);

const openDeal = deal => {
  if (openLinkedInPlace) {
    openLinkedInPlace('deal', deal.id);
    return;
  }
  dealDetailsDialogRef.value?.open(deal.id);
};

const formattedValue = deal => {
  if (!deal.value) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: deal.currency || 'BRL',
  }).format(deal.value);
};

const assigneeFor = deal => {
  if (!deal.assignedToId) return null;
  const agent = getAgentById.value(deal.assignedToId);
  return agent?.id ? agent : null;
};

// Recarrega a lista quando algo muda nos niveis abertos pelo LinkedEntityPane
const linkedRevision = inject('linkedRevision', null);
if (linkedRevision) watch(linkedRevision, loadContactDeals);

watch(() => props.contactId, loadContactDeals);
watch(() => props.companyId, loadContactDeals);
onMounted(() => {
  loadContactDeals();
  store.dispatch('agents/get');
});
</script>

<template>
  <div class="flex flex-col gap-1">
    <button
      v-if="!hasDeals"
      type="button"
      class="flex items-center w-full gap-3 p-2 -mx-2 text-left transition-colors rounded-lg hover:bg-n-slate-2 dark:hover:bg-n-solid-2"
      @click="openCreateDialog"
    >
      <span
        class="i-lucide-dollar-sign text-xl text-n-slate-10 flex-shrink-0"
      />
      <div class="flex flex-col min-w-0">
        <span class="text-sm text-n-slate-11">
          {{ t('CONVERSATION.HEADER.DEAL_ACTION.EMPTY') }}
        </span>
        <span class="text-xs text-n-blue-11">
          {{ t('CONVERSATION.HEADER.DEAL_ACTION.CREATE') }}
        </span>
      </div>
    </button>

    <template v-else>
      <button
        v-for="deal in contactDeals"
        :key="deal.id"
        type="button"
        class="flex items-center w-full gap-3 p-2 -mx-2 text-left group/deal"
        :class="
          highlightRows
            ? 'px-3 py-3 mx-0 transition-all duration-300 ease-in-out rounded-none hover:rounded-xl hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3'
            : 'transition-colors rounded-lg hover:bg-n-slate-2 dark:hover:bg-n-solid-2'
        "
        @click="openDeal(deal)"
      >
        <span
          class="i-lucide-dollar-sign text-xl text-n-slate-10 flex-shrink-0"
        />
        <div class="flex flex-col min-w-0 flex-1">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ deal.name }}
          </span>
          <div class="flex items-center gap-1.5 min-w-0">
            <span
              v-if="deal.pipelineStage"
              class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium text-white truncate max-w-[60%]"
              :style="{
                backgroundColor: deal.pipelineStage.color || '#6366f1',
              }"
            >
              {{ deal.pipelineStage.name }}
            </span>
            <span
              v-if="formattedValue(deal)"
              class="text-xs truncate text-n-slate-11"
            >
              {{ formattedValue(deal) }}
            </span>
          </div>
        </div>
        <Avatar
          v-if="assigneeFor(deal)"
          :name="assigneeFor(deal).name || assigneeFor(deal).email"
          :src="assigneeFor(deal).thumbnail"
          :size="24"
          rounded-full
          class="flex-shrink-0"
        />
        <span
          v-if="highlightRows"
          class="i-lucide-chevron-right size-4 shrink-0 text-n-slate-10 transition-transform duration-300 ease-in-out group-hover/deal:translate-x-0.5 group-hover/deal:text-n-slate-12"
          aria-hidden="true"
        />
      </button>

      <button
        type="button"
        class="self-start flex items-center gap-1 mt-1 text-xs text-n-blue-11"
        @click="openCreateDialog"
      >
        <span class="i-lucide-plus text-xs" />
        {{ t('CONVERSATION.HEADER.DEAL_ACTION.CREATE_NEW') }}
      </button>
    </template>

    <DealDetailsDialog ref="dealDetailsDialogRef" @updated="loadContactDeals" />
  </div>
</template>
